import React, { FC } from 'react'
import { Chip, Tooltip } from '@mui/material'
import format from 'date-fns/format'
import { GridColDef } from '@mui/x-data-grid-premium'
import { ContractorTransfer } from 'src/api'
import { InnerLink } from 'src/components/ui'
import { getTransferStatusMeta } from './contractorTransferHelpers'

const formatDate = (value?: string | null): string => (value ? format(new Date(value), 'PPpp') : '-')

interface ContractorTransferStatusChipProps {
  status?: ContractorTransfer['status']
  error?: string | null
  size?: 'small' | 'medium'
}

export const ContractorTransferStatusChip: FC<ContractorTransferStatusChipProps> = ({ status, error, size = 'small' }) => {
  const { label, color, description } = getTransferStatusMeta(status)
  const tooltip = [description, error].filter(Boolean).join('. ')
  const chip = <Chip label={label} color={color} size={size} />
  return tooltip ? <Tooltip title={tooltip}>{chip}</Tooltip> : chip
}

export namespace ContractorTransferColumnsDataGrid {
  export const Id: GridColDef<ContractorTransfer> = {
    field: 'id',
    headerName: 'ID',
    type: 'string',
    minWidth: 90,
    editable: false,
    sortable: false,
    renderCell: ({ row }) => <InnerLink to={`/settings/transfers/${row.id}`}>{`#${row.id}`}</InnerLink>
  }

  export const Source: GridColDef<ContractorTransfer> = {
    field: 'sourceContractor',
    headerName: 'Source',
    type: 'string',
    minWidth: 200,
    flex: 1,
    editable: false,
    sortable: false,
    valueGetter: ({ row }) => row.sourceContractor?.name || `#${row.sourceContractorId}`,
    renderCell: ({ value, row }) => <InnerLink to={`/contractors/${row.sourceContractorId}`}>{value}</InnerLink>
  }

  export const Target: GridColDef<ContractorTransfer> = {
    field: 'targetContractor',
    headerName: 'Target',
    type: 'string',
    minWidth: 200,
    flex: 1,
    editable: false,
    sortable: false,
    valueGetter: ({ row }) => row.targetContractor?.name || `#${row.targetContractorId}`,
    renderCell: ({ value, row }) => <InnerLink to={`/contractors/${row.targetContractorId}`}>{value}</InnerLink>
  }

  export const Author: GridColDef<ContractorTransfer> = {
    field: 'author',
    headerName: 'Author',
    type: 'string',
    minWidth: 160,
    editable: false,
    sortable: false,
    valueGetter: ({ row }) => row.author?.name || '-',
    renderCell: ({ value, row }) => (row.author?.id ? <InnerLink to={`/users/${row.author.id}`}>{value}</InnerLink> : value)
  }

  export const Status: GridColDef<ContractorTransfer> = {
    field: 'status',
    headerName: 'Status',
    type: 'string',
    minWidth: 190,
    editable: false,
    sortable: false,
    valueGetter: ({ row }) => getTransferStatusMeta(row.status).label,
    renderCell: ({ row }) => <ContractorTransferStatusChip status={row.status} error={row.error} />
  }

  export const CreatedAt: GridColDef<ContractorTransfer> = {
    field: 'createdAt',
    headerName: 'Started',
    type: 'string',
    minWidth: 190,
    editable: false,
    sortable: false,
    valueFormatter: ({ value }) => formatDate(value)
  }

  export const FinishedAt: GridColDef<ContractorTransfer> = {
    field: 'finishedAt',
    headerName: 'Finished',
    type: 'string',
    minWidth: 190,
    editable: false,
    sortable: false,
    valueFormatter: ({ value }) => formatDate(value)
  }
}
